/* eslint-disable react/require-default-props */
import React, {
  useEffect, forwardRef, useRef, useCallback
} from 'react';
import { node, func, oneOf } from 'prop-types';
import { useHeaderObserver } from '@thd-olt-functional/utils';
import classNames from 'classnames';
import { getTopHatOffset, useMobileBreakpoint } from '../../utils/headerUtils';
import { HeaderDrawerBackdrop } from '../drawer-ui/HeaderDrawerBackdrop';
import useOutsideClick from '../../hooks/useOutsideClick';
import { Portal } from './Portal';
import { CONSTANTS } from '../../constants';
import { FocusBoundary } from './FocusBoundary';

/** solution to the missing features of the Stencil-UI Drawer
 * `<AnchoredDrawer />`: responsive & nested stencil drawer that appears at the bottom of the header.
 */

const AnchoredDrawer = forwardRef(({
  children = null,
  position = 'right',
  onClickBackdrop = () => { }
}, ref) => {
  const isMobile = useMobileBreakpoint();

  const presentationRef = useRef(null);
  const backdropRef = useRef(null);

  const [entry] = useHeaderObserver();

  useOutsideClick(ref, onClickBackdrop);

  const getAnchorPosition = useCallback(() => {
    const { TOPHAT_HEIGHT_OFFSET } = CONSTANTS;

    const topHatOffset = getTopHatOffset();

    if (!entry?.visible) {
      return {
        backdrop: {
          top: 0,
          bottom: 0
        },
        drawer: {
          top: isMobile ? TOPHAT_HEIGHT_OFFSET : 0,
          bottom: 0
        }
      };
    }

    return {
      backdrop: {
        top: !entry?.navigation?.positionBottom ? entry?.root?.clientRect?.height + topHatOffset : 0,
        bottom: entry?.navigation?.positionBottom ? entry?.navigation.clientRect?.height : 0,
      },
      drawer: {
        top: entry?.navigation?.positionBottom ? TOPHAT_HEIGHT_OFFSET : 0,
        bottom: entry?.navigation?.positionBottom ? entry?.navigation.clientRect?.height : 0,
      }
    };
  }, [entry]);

  const getPositioningStyle = useCallback(() => {
    const anchorPosition = getAnchorPosition();
    return {
      backdrop: {
        top: `${Math.round(anchorPosition.backdrop.top)}px`,
        left: '0px',
        right: '0px',
        bottom: `${Math.round(anchorPosition.backdrop.bottom)}px`,
        opacity: 1
      },
      drawer: {
        top: `${Math.round(anchorPosition.drawer.top)}px`,
        left: '0px',
        right: '0px',
        bottom: `${Math.round(anchorPosition.drawer.bottom)}px`,
        opacity: 1
      }
    };

  }, [entry]);

  const setPositioningStyles = useCallback(() => {
    const styles = getPositioningStyle();

    const { current: backdropEl } = backdropRef;
    const { current: presentationEl } = presentationRef;
    const { current: drawerEl } = ref;

    if (!backdropEl || !presentationEl || !drawerEl) return;

    Object.assign(backdropEl.style, styles.backdrop);
    Object.assign(presentationEl.style, styles.backdrop);
    Object.assign(drawerEl.style, styles.drawer);
  }, [getPositioningStyle]);

  const dynamicDrawerStyles = classNames(
    /* shared styles for all screens */
    'sui-bg-primary sui-transition-opacity sui-ease-in-out sui-duration-300',
    'sui-outline-none',
    /* default & small-medium screen drawer */
    'sui-fixed',
    'sui-w-full',
    'sui-rounded-t-lg',
    'sui-shadow-none',
    'sui-overflow-y-scroll',
    /* large screen drawer */
    'lg:sui-relative lg:sui-h-full',
    'lg:sui-row-span-full lg:sui-col-span-5',
    'lg:sui-rounded-base lg:sui-shadow-lg',
    /* very large screen drawer */
    'xl:sui-row-start-1 xl:sui-row-span-full xl:sui-col-span-4',
    'xl:sui-rounded-base xl:sui-shadow-xl',
    {
      /* large screen drawer props */
      'lg:sui-col-start-8': (position === 'right'),
      'lg:sui-col-start-1': (position === 'left'),
      'xl:sui-col-start-9': (position === 'right'),
      'xl:sui-col-start-1': (position === 'left')
    }
  );

  const gridClasses = [
    /* grid styles for positioning/sizing of left/right drawers */
    'sui-grid',
    'sui-grid-cols-12',
    'sui-grid-rows-6',
    'sui-gap-0',
    'sui-h-full',
    'sui-outline-none'
  ].join(' ');

  useEffect(() => {
    setPositioningStyles();
  }, [entry?.visible, entry?.navigation?.positionBottom]);

  return (
    <Portal>
      <div id="header-anchor-drawer-root" className="hfapp" data-component="HeaderAnchoredDrawer">
        <div
          ref={presentationRef}
          role="presentation"
          className="sui-z-header-drawer sui-fixed sui-transition-opacity sui-ease-in-out sui-duration-300"
          style={{ opacity: 0 }}
        >
          <HeaderDrawerBackdrop ref={backdropRef} open styles={{ opacity: 0 }} />
          <FocusBoundary open>
            <div tabIndex={-1} className={gridClasses}>
              <div
                id="header-anchor-drawer"
                ref={ref}
                className={dynamicDrawerStyles}
                style={{ opacity: 0 }}
              >
                {children}
              </div>
            </div>
          </FocusBoundary>
        </div>
      </div>
    </Portal>
  );
});

AnchoredDrawer.displayName = 'Drawer';

AnchoredDrawer.propTypes = {
  /**
   * Drawer children, usually the included subcomponents.
   */
  children: node,
  /**
   * Placement of the drawer.
   */
  position: oneOf(['left', 'right']),
  /**
   * onClick for when use clicks Backdrop area.
   */
  onClickBackdrop: func
};

export { AnchoredDrawer };
